import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  ChevronLeft,
  ChevronDown,
  Calendar,
  Clock,
  TrendingUp,
  Sparkles,
  AlertTriangle,
  Trash2,
  FileText,
  Zap,
  Send,
  Plus,
  X,
} from 'lucide-react'
import type {
  StreamDetail,
  ScheduleType,
  AccountUser,
} from '@/types/streams'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import { OptionalBadge } from '@/components/ui/OptionalBadge'

interface UserCheckboxListProps {
  users: AccountUser[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  label?: string
}

function UserCheckboxList({ users, selectedIds, onChange, label }: UserCheckboxListProps) {
  const toggleUser = (userId: string) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter(id => id !== userId))
    } else {
      onChange([...selectedIds, userId])
    }
  }

  if (!users || users.length === 0) {
    return (
      <p className="text-xs text-stone-400 dark:text-stone-500 italic">
        No users available
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide">
          {label}
        </label>
      )}
      <div className="space-y-1">
        {users.map(user => (
          <label key={user.id} className="flex items-center gap-2 cursor-pointer py-1">
            <input
              type="checkbox"
              checked={selectedIds.includes(user.id)}
              onChange={() => toggleUser(user.id)}
              className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-stone-700 dark:text-stone-300">
              {user.name}
              {user.isOwner && (
                <span className="ml-1 text-xs text-stone-400">(Owner)</span>
              )}
            </span>
            <span className="text-xs text-stone-400">{user.email}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

interface SettingsCardProps {
  icon: React.ReactNode
  title: string
  description?: string
  children: React.ReactNode
  defaultExpanded?: boolean
}

function SettingsCard({ icon, title, description, children, defaultExpanded = false }: SettingsCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  return (
    <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-5 flex items-center justify-between hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-stone-500 dark:text-stone-400">
            {icon}
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-stone-900 dark:text-stone-100">
              {title}
            </h3>
            {description && (
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                {description}
              </p>
            )}
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-stone-400 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isExpanded && (
        <div className="px-6 py-5 border-t border-stone-100 dark:border-stone-800">
          {children}
        </div>
      )}
    </div>
  )
}

export interface StreamSettingsProps {
  stream: StreamDetail
  onSave?: (settings: StreamSettingsData) => void
  onCancel?: () => void
  onDelete?: () => void
}

export interface StreamSettingsData {
  name: string
  description: string | null
  aiContext: string | null
  // Report generation settings
  characterThreshold: number
  reportScheduleType: ScheduleType
  reportScheduleDay: number | null
  reportScheduleTime: string | null
  maxItemsPerReport: number | null
  minItemsPerReport: number | null
  // Delivery settings
  deliveryScheduleType: ScheduleType
  deliveryScheduleDay: number | null
  deliveryScheduleTime: string | null
  notifyImmediately: boolean
  reportSubscriberIds: string[]
  // Trend settings
  trendLookbackReports: number | null
  trendLookbackDays: number | null
  // Webhook delivery settings
  webhookEnabled: boolean
  webhookUrl: string | null
  webhookSecretToken: string | null
  webhookHeaders: { key: string; value: string }[]
}

const SCHEDULE_OPTIONS = ['none', 'daily', 'weekly', 'monthly', 'quarterly', 'annually'] as const
const DELIVERY_SCHEDULE_OPTIONS = ['daily', 'weekly', 'monthly', 'quarterly', 'annually'] as const

export function StreamSettings({
  stream,
  onSave,
  onCancel,
  onDelete,
}: StreamSettingsProps) {
  // Basic info state
  const [name, setName] = useState(stream.name)
  const [description, setDescription] = useState(stream.description || '')

  // Report generation settings state
  const [characterThreshold, setCharacterThreshold] = useState(stream.characterThreshold || 150000)
  const [reportScheduleType, setReportScheduleType] = useState<ScheduleType>(stream.reportScheduleType)
  const [reportScheduleDay, setReportScheduleDay] = useState<number | null>(stream.reportScheduleDay)
  const [reportScheduleTime, setReportScheduleTime] = useState(stream.reportScheduleTime || '09:00')
  const [maxItemsPerReport, setMaxItemsPerReport] = useState<number | null>(stream.maxItemsPerReport)
  const [minItemsPerReport, setMinItemsPerReport] = useState<number | null>(stream.minItemsPerReport)

  // Delivery settings state
  const [deliveryScheduleType, setDeliveryScheduleType] = useState<ScheduleType>(stream.deliveryScheduleType)
  const [deliveryScheduleDay, setDeliveryScheduleDay] = useState<number | null>(stream.deliveryScheduleDay)
  const [deliveryScheduleTime, setDeliveryScheduleTime] = useState(stream.deliveryScheduleTime || '09:00')
  const [notifyImmediately, setNotifyImmediately] = useState(stream.notifyImmediately !== false)
  const [reportSubscriberIds, setReportSubscriberIds] = useState<string[]>(stream.reportSubscriberIds || [])

  // Webhook delivery settings state
  const defaultWebhookHeaders = [
    { key: 'title', value: '{{report_title}}' },
    { key: 'content', value: '{{report_content}}' },
    { key: 'date', value: '{{report_date}}' },
  ]
  const [webhookEnabled, setWebhookEnabled] = useState(stream.webhookEnabled || false)
  const [webhookUrl, setWebhookUrl] = useState(stream.webhookUrl || '')
  const [webhookSecretToken, setWebhookSecretToken] = useState(stream.webhookSecretToken || '')
  const [webhookHeaders, setWebhookHeaders] = useState<{ key: string; value: string }[]>(
    stream.webhookHeaders?.length ? stream.webhookHeaders : defaultWebhookHeaders
  )

  // Other settings state
  const [aiContext, setAiContext] = useState(stream.aiContext || '')
  const [trendLookbackReports, setTrendLookbackReports] = useState<number | null>(stream.trendLookbackReports)
  const [trendLookbackDays, setTrendLookbackDays] = useState<number | null>(stream.trendLookbackDays)

  // Derive initial lookback type from data
  const getInitialLookbackType = (): 'none' | 'reports' | 'days' => {
    if (stream.trendLookbackReports) return 'reports'
    if (stream.trendLookbackDays) return 'days'
    return 'none'
  }
  const [trendLookbackType, setTrendLookbackType] = useState<'none' | 'reports' | 'days'>(getInitialLookbackType())

  // UI state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Check if there are unsaved changes
  const hasChanges =
    name !== stream.name ||
    description !== (stream.description || '') ||
    aiContext !== (stream.aiContext || '') ||
    characterThreshold !== (stream.characterThreshold || 150000) ||
    reportScheduleType !== stream.reportScheduleType ||
    reportScheduleDay !== stream.reportScheduleDay ||
    reportScheduleTime !== (stream.reportScheduleTime || '09:00') ||
    maxItemsPerReport !== stream.maxItemsPerReport ||
    minItemsPerReport !== stream.minItemsPerReport ||
    deliveryScheduleType !== stream.deliveryScheduleType ||
    deliveryScheduleDay !== stream.deliveryScheduleDay ||
    deliveryScheduleTime !== (stream.deliveryScheduleTime || '09:00') ||
    notifyImmediately !== (stream.notifyImmediately !== false) ||
    trendLookbackReports !== stream.trendLookbackReports ||
    trendLookbackDays !== stream.trendLookbackDays ||
    JSON.stringify(reportSubscriberIds.sort()) !== JSON.stringify((stream.reportSubscriberIds || []).sort()) ||
    webhookEnabled !== (stream.webhookEnabled || false) ||
    webhookUrl !== (stream.webhookUrl || '') ||
    JSON.stringify(webhookHeaders) !== JSON.stringify(stream.webhookHeaders?.length ? stream.webhookHeaders : defaultWebhookHeaders)

  const handleSave = () => {
    onSave?.({
      name,
      description: description || null,
      aiContext: aiContext || null,
      characterThreshold,
      reportScheduleType,
      reportScheduleDay,
      reportScheduleTime,
      maxItemsPerReport,
      minItemsPerReport,
      deliveryScheduleType: notifyImmediately ? null : deliveryScheduleType,
      deliveryScheduleDay: notifyImmediately ? null : deliveryScheduleDay,
      deliveryScheduleTime: notifyImmediately ? null : deliveryScheduleTime,
      notifyImmediately,
      reportSubscriberIds,
      trendLookbackReports,
      trendLookbackDays,
      webhookEnabled,
      webhookUrl: webhookUrl || null,
      webhookSecretToken: webhookSecretToken || null,
      webhookHeaders,
    })
  }

  const handleDiscard = () => {
    setName(stream.name)
    setDescription(stream.description || '')
    setAiContext(stream.aiContext || '')
    setCharacterThreshold(stream.characterThreshold || 150000)
    setReportScheduleType(stream.reportScheduleType)
    setReportScheduleDay(stream.reportScheduleDay)
    setReportScheduleTime(stream.reportScheduleTime || '09:00')
    setMaxItemsPerReport(stream.maxItemsPerReport)
    setMinItemsPerReport(stream.minItemsPerReport)
    setDeliveryScheduleType(stream.deliveryScheduleType)
    setDeliveryScheduleDay(stream.deliveryScheduleDay)
    setDeliveryScheduleTime(stream.deliveryScheduleTime || '09:00')
    setNotifyImmediately(stream.notifyImmediately !== false)
    setReportSubscriberIds(stream.reportSubscriberIds || [])
    setTrendLookbackReports(stream.trendLookbackReports)
    setTrendLookbackDays(stream.trendLookbackDays)
    setTrendLookbackType(getInitialLookbackType())
    setWebhookEnabled(stream.webhookEnabled || false)
    setWebhookUrl(stream.webhookUrl || '')
    setWebhookSecretToken(stream.webhookSecretToken || '')
    setWebhookHeaders(stream.webhookHeaders?.length ? stream.webhookHeaders : defaultWebhookHeaders)
  }

  // Handle lookback type change
  const handleLookbackTypeChange = (type: 'none' | 'reports' | 'days') => {
    setTrendLookbackType(type)
    if (type === 'none') {
      setTrendLookbackReports(null)
      setTrendLookbackDays(null)
    } else if (type === 'reports') {
      setTrendLookbackDays(null)
      if (!trendLookbackReports) setTrendLookbackReports(3) // default
    } else if (type === 'days') {
      setTrendLookbackReports(null)
      if (!trendLookbackDays) setTrendLookbackDays(30) // default
    }
  }

  const renderScheduleFields = (
    type: ScheduleType,
    day: number | null,
    time: string,
    onTypeChange: (t: ScheduleType) => void,
    onDayChange: (d: number | null) => void,
    onTimeChange: (t: string) => void,
    nextAt: string | null,
    label: string,
    excludeNone = false
  ) => {
    const options = excludeNone ? DELIVERY_SCHEDULE_OPTIONS : SCHEDULE_OPTIONS
    const gridCols = excludeNone ? 'grid-cols-5' : 'grid-cols-6'

    return (
    <div className="space-y-4">
      <div>
        <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">
          {label}
        </label>
        <div className={`grid ${gridCols} gap-2`}>
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onTypeChange(opt === 'none' ? null : opt)
                if (opt === 'weekly' && day === null) onDayChange(1)
                if (['monthly', 'quarterly', 'annually'].includes(opt) && day === null) onDayChange(1)
              }}
              className={`px-2 py-2 text-xs rounded-xl transition-all ${
                (type === opt) || (opt === 'none' && !type)
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
              }`}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {type && type !== 'none' && (
        <>
          {type === 'weekly' && (
            <div>
              <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                Day of Week
              </label>
              <select
                value={day ?? 1}
                onChange={(e) => onDayChange(parseInt(e.target.value))}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
            </div>
          )}

          {['monthly', 'quarterly', 'annually'].includes(type) && (
            <div>
              <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                Day of {type === 'monthly' ? 'Month' : type === 'quarterly' ? 'Quarter' : 'Year'}
              </label>
              <input
                type="number"
                min={1}
                max={type === 'annually' ? 365 : 28}
                value={day ?? 1}
                onChange={(e) => onDayChange(parseInt(e.target.value))}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
              Time ({stream.userTimezoneAbbr})
            </label>
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-stone-400" />
              <input
                type="time"
                value={time}
                onChange={(e) => onTimeChange(e.target.value)}
                className="px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </>
      )}
    </div>
  )}

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-3xl mx-auto px-6 py-6">
          {/* Breadcrumb */}
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Back to {stream.name}</span>
          </button>

          <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
            Settings
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
            Configure report generation and notification preferences
          </p>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Stream Info Card */}
        <SettingsCard
          icon={<FileText className="w-5 h-5" />}
          title="Stream Info"
          description="Basic stream identification"
        >
          <div className="space-y-4">
            <div>
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                Name
                <InfoTooltip content="The display name for this data stream. This appears in reports and notifications." />
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Stream name"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                Description
                <OptionalBadge />
                <InfoTooltip content="Optional internal notes about this stream. Not shown in reports." />
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description for this stream..."
                rows={2}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
        </SettingsCard>

        {/* AI Context Card */}
        <SettingsCard
          icon={<Sparkles className="w-5 h-5" />}
          title="AI Context"
          description="Help the AI better understand your data"
        >
          <div>
            <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
              Custom Instructions
              <OptionalBadge />
              <InfoTooltip content="Custom instructions for the AI when analyzing items and generating reports. This context helps the AI understand your specific use case." />
            </label>
            <textarea
              value={aiContext}
              onChange={(e) => setAiContext(e.target.value)}
              placeholder="e.g., This stream contains customer support tickets for our SaaS product. Focus on identifying common pain points and feature requests..."
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
          </div>
        </SettingsCard>

        {/* Report Generation Card */}
        <SettingsCard
          icon={<Zap className="w-5 h-5" />}
          title="Report Generation"
          description="Configure when reports are automatically generated"
        >
          <div className="space-y-6">
            {/* Character Threshold */}
            <div>
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                Character Threshold
                <InfoTooltip content="Reports auto-generate when accumulated item content reaches this threshold. Range: 10,000 - 500,000 characters." />
              </label>
              <input
                type="number"
                min={10000}
                max={500000}
                step={10000}
                value={characterThreshold}
                onChange={(e) => setCharacterThreshold(Math.min(500000, Math.max(10000, parseInt(e.target.value) || 150000)))}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Progress meter */}
            <div className="p-4 rounded-xl bg-teal-50 dark:bg-teal-950/30 border border-teal-100 dark:border-teal-900/50">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-teal-700 dark:text-teal-300 font-medium">
                  {stream.unprocessedItemsCount} items pending analysis
                </span>
                <span className="text-teal-700 dark:text-teal-300">
                  {stream.currentCharacterCount.toLocaleString()} / {characterThreshold.toLocaleString()} chars
                </span>
              </div>
              <div className="w-full h-2 bg-teal-200 dark:bg-teal-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-600 dark:bg-teal-500 transition-all duration-300"
                  style={{ width: `${Math.min(100, (stream.currentCharacterCount / characterThreshold) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-teal-600 dark:text-teal-400 mt-2">
                Next report will auto-generate when character threshold is reached
              </p>
            </div>

            {/* Report Generation Schedule */}
            <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
              <div className="flex items-center mb-3">
                <span className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide">
                  Report Generation Schedule
                </span>
                <OptionalBadge />
                <InfoTooltip content="Set a recurring schedule to generate reports. Reports will generate when the schedule arrives OR when the character threshold is reached, whichever comes first." />
              </div>
              {renderScheduleFields(
                reportScheduleType,
                reportScheduleDay,
                reportScheduleTime,
                setReportScheduleType,
                setReportScheduleDay,
                setReportScheduleTime,
                stream.nextScheduledReportAt,
                ''
              )}
            </div>

            {/* Max Items Per Report */}
            <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                Max Items Per Report
                <OptionalBadge />
                <InfoTooltip content="Maximum number of items to include in a single report. When reached, a report will be generated even if the character threshold hasn't been met." />
              </label>
              <input
                type="number"
                min={1}
                max={300}
                value={maxItemsPerReport ?? ''}
                placeholder="No limit"
                onChange={(e) => setMaxItemsPerReport(e.target.value ? Math.min(300, Math.max(1, parseInt(e.target.value))) : null)}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                Maximum 300 items per report. Leave empty for no limit.
              </p>
            </div>

            {/* Min Items Per Report */}
            <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                Min Items Per Report
                <OptionalBadge />
                <InfoTooltip content="Minimum number of items required before a scheduled report will generate. Does not apply to threshold-triggered reports." />
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={minItemsPerReport ?? ''}
                placeholder="No minimum"
                onChange={(e) => setMinItemsPerReport(e.target.value ? Math.min(100, Math.max(1, parseInt(e.target.value))) : null)}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </SettingsCard>

        {/* Report Delivery Card */}
        <SettingsCard
          icon={<Send className="w-5 h-5" />}
          title="Report Delivery"
          description="Configure when and how report notifications are sent"
        >
          <div className="space-y-6">
            {/* Email Recipients */}
            <div>
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">
                Email Recipients
                <InfoTooltip content="Select which users receive email notifications when reports are generated." />
              </label>
              <UserCheckboxList
                users={stream.accountUsers || []}
                selectedIds={reportSubscriberIds}
                onChange={setReportSubscriberIds}
              />
            </div>

            {/* Send to Webhook */}
            <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">
                Send to Webhook
                <InfoTooltip content="Send report data to an external webhook URL when reports are delivered." />
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={webhookEnabled}
                  onChange={(e) => setWebhookEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-stone-700 dark:text-stone-300">
                  Enable webhook delivery
                </span>
              </label>

              {webhookEnabled && (
                <div className="mt-4 p-4 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 space-y-4">
                  {/* Webhook URL */}
                  <div>
                    <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                      Webhook URL
                    </label>
                    <input
                      type="url"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://example.com/webhook"
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>

                  {/* Authorization Token */}
                  <div>
                    <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                      Authorization Token
                    </label>
                    <input
                      type="password"
                      value={webhookSecretToken}
                      onChange={(e) => setWebhookSecretToken(e.target.value)}
                      placeholder="Bearer token for webhook authentication"
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <p className="text-xs text-stone-400 dark:text-stone-500 mt-1">
                      Optional. Sent as a Bearer token in the Authorization header.
                    </p>
                  </div>

                  {/* Key/Value Pairs */}
                  <div>
                    <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                      Payload Fields
                    </label>
                    <p className="text-xs text-stone-400 dark:text-stone-500 mb-3">
                      Define key/value pairs for the webhook JSON payload. Use template tags like <code className="bg-stone-200 dark:bg-stone-700 px-1 rounded">{'{{report_title}}'}</code>, <code className="bg-stone-200 dark:bg-stone-700 px-1 rounded">{'{{report_content}}'}</code>, <code className="bg-stone-200 dark:bg-stone-700 px-1 rounded">{'{{report_date}}'}</code> for dynamic content.
                    </p>
                    <div className="space-y-2">
                      {webhookHeaders.map((header, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={header.key}
                            onChange={(e) => {
                              const updated = [...webhookHeaders]
                              updated[index] = { ...updated[index], key: e.target.value }
                              setWebhookHeaders(updated)
                            }}
                            placeholder="Key"
                            className="w-1/3 px-3 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                          <input
                            type="text"
                            value={header.value}
                            onChange={(e) => {
                              const updated = [...webhookHeaders]
                              updated[index] = { ...updated[index], value: e.target.value }
                              setWebhookHeaders(updated)
                            }}
                            placeholder="Value"
                            className="flex-1 px-3 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                          <button
                            onClick={() => {
                              setWebhookHeaders(webhookHeaders.filter((_, i) => i !== index))
                            }}
                            className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                            title="Remove field"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setWebhookHeaders([...webhookHeaders, { key: '', value: '' }])}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Field
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Recurring Delivery Schedule */}
            <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">
                Recurring Delivery Schedule
                <InfoTooltip content="Choose when to send report notifications. 'Notify Immediately' sends emails as soon as reports are generated." />
              </label>

              <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                  onClick={() => setNotifyImmediately(true)}
                  className={`px-4 py-3 text-sm rounded-xl transition-all text-left ${
                    notifyImmediately
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
                  }`}
                >
                  <p className="font-medium">Notify Immediately</p>
                  <p className={`text-xs mt-0.5 ${notifyImmediately ? 'text-teal-100' : 'text-stone-500 dark:text-stone-400'}`}>
                    Email sent when report is ready
                  </p>
                </button>
                <button
                  onClick={() => {
                    setNotifyImmediately(false)
                    if (!deliveryScheduleType) setDeliveryScheduleType('daily')
                  }}
                  className={`px-4 py-3 text-sm rounded-xl transition-all text-left ${
                    !notifyImmediately
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
                  }`}
                >
                  <p className="font-medium">Send Reports on Schedule...</p>
                  <p className={`text-xs mt-0.5 ${!notifyImmediately ? 'text-teal-100' : 'text-stone-500 dark:text-stone-400'}`}>
                    Send emails on a recurring schedule
                  </p>
                </button>
              </div>

              {/* Schedule controls (only shown when not immediate) */}
              {!notifyImmediately && (
                <div className="p-4 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700">
                  {renderScheduleFields(
                    deliveryScheduleType,
                    deliveryScheduleDay,
                    deliveryScheduleTime,
                    setDeliveryScheduleType,
                    setDeliveryScheduleDay,
                    setDeliveryScheduleTime,
                    stream.nextDeliveryAt,
                    'Delivery Schedule',
                    true // exclude "None" option - users should choose immediate or pick a schedule
                  )}
                </div>
              )}
            </div>
          </div>
        </SettingsCard>

        {/* Trend Analysis Card */}
        <SettingsCard
          icon={<TrendingUp className="w-5 h-5" />}
          title="Trend Analysis"
          description="Compare reports against historical data to identify patterns"
        >
          <div className="space-y-4">
            <div>
              <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">
                Lookback Type
                <OptionalBadge />
                <InfoTooltip content="Choose how to select historical data for trend analysis: by number of past reports or by time period." />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleLookbackTypeChange('none')}
                  className={`px-4 py-3 text-sm rounded-xl transition-all text-left ${
                    trendLookbackType === 'none'
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
                  }`}
                >
                  <p className="font-medium">None</p>
                  <p className={`text-xs mt-0.5 ${trendLookbackType === 'none' ? 'text-teal-100' : 'text-stone-500 dark:text-stone-400'}`}>
                    No trend analysis
                  </p>
                </button>
                <button
                  onClick={() => handleLookbackTypeChange('reports')}
                  className={`px-4 py-3 text-sm rounded-xl transition-all text-left ${
                    trendLookbackType === 'reports'
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
                  }`}
                >
                  <p className="font-medium">By Reports</p>
                  <p className={`text-xs mt-0.5 ${trendLookbackType === 'reports' ? 'text-teal-100' : 'text-stone-500 dark:text-stone-400'}`}>
                    Last N reports
                  </p>
                </button>
                <button
                  onClick={() => handleLookbackTypeChange('days')}
                  className={`px-4 py-3 text-sm rounded-xl transition-all text-left ${
                    trendLookbackType === 'days'
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
                  }`}
                >
                  <p className="font-medium">By Days</p>
                  <p className={`text-xs mt-0.5 ${trendLookbackType === 'days' ? 'text-teal-100' : 'text-stone-500 dark:text-stone-400'}`}>
                    Last N days
                  </p>
                </button>
              </div>
            </div>

            {/* Show input based on selected type */}
            {trendLookbackType === 'reports' && (
              <div className="p-4 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700">
                <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                  Number of Reports
                  <InfoTooltip content="Include this many past reports when analyzing trends." />
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={trendLookbackReports ?? ''}
                  placeholder="e.g., 3"
                  onChange={(e) => setTrendLookbackReports(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                  Recommended: 3-5 reports for meaningful trend data
                </p>
              </div>
            )}

            {trendLookbackType === 'days' && (
              <div className="p-4 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700">
                <label className="flex items-center text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                  Number of Days
                  <InfoTooltip content="Include reports from the past N days when analyzing trends." />
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={trendLookbackDays ?? ''}
                  placeholder="e.g., 30"
                  onChange={(e) => setTrendLookbackDays(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                  Recommended: 30-90 days for weekly reports
                </p>
              </div>
            )}
          </div>
        </SettingsCard>

        {/* Danger Zone */}
        <div className="pt-6">
          <div className="bg-red-50 dark:bg-red-950/20 rounded-2xl border border-red-200 dark:border-red-900/50 overflow-hidden">
            <div className="px-6 py-4 border-b border-red-100 dark:border-red-900/50">
              <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">
                Danger Zone
              </h3>
            </div>
            <div className="px-6 py-5">
              {showDeleteConfirm ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <p className="text-sm text-red-700 dark:text-red-300">
                      Delete this stream permanently?
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-3 py-1.5 text-sm text-stone-600 dark:text-stone-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        onDelete?.()
                        setShowDeleteConfirm(false)
                      }}
                      className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete this stream...
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Sticky save bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-amber-500 dark:bg-amber-600 shadow-lg z-20">
          <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
            <p className="text-sm font-medium text-amber-950 dark:text-white">
              You have unsaved changes
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDiscard}
                className="px-3 py-1.5 text-sm font-medium text-amber-950 dark:text-white hover:text-amber-800 dark:hover:text-amber-100"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-1.5 text-sm font-medium bg-amber-950 hover:bg-amber-900 text-white rounded-lg transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
